import type { Call } from '@twilio/voice-sdk';
import type { Room } from 'twilio-video';

type Listener = (val: any) => void;
type RemotePublicationLike = {
  trackSid?: string;
  track?: {
    kind?: string;
    mediaStreamTrack?: MediaStreamTrack;
  } | null;
};
type RemoteParticipantLike = {
  sid?: string;
  audioTracks?: {
    forEach: (callback: (publication: RemotePublicationLike) => void) => void;
  };
  on?: (event: string, listener: (...args: any[]) => void) => void;
  removeListener?: (event: string, listener: (...args: any[]) => void) => void;
};
type LocalAudioTrackLike = {
  kind?: string;
  isEnabled?: boolean;
};
type LocalPublicationLike = {
  track?: LocalAudioTrackLike | null;
};
type LocalParticipantLike = {
  audioTracks?: {
    forEach: (callback: (publication: LocalPublicationLike) => void) => void;
  };
  on?: (event: string, listener: (...args: any[]) => void) => void;
  removeListener?: (event: string, listener: (...args: any[]) => void) => void;
};

/*
This is a singleton approach  - only one audio stream is created.
By doing so, the data for that one audio stream is shared so
different React components will have the same data
*/

let isRecordingState = false;
let isInitializing = false;
let isAudioErrorState = false;
let isRecordingPausedState = false;
let audioContext: AudioContext | null = null;
let mediaStream: MediaStream | null = null;
let workletNode: AudioWorkletNode | null = null;
let localSourceNode: MediaStreamAudioSourceNode | null = null;
let localGainNode: GainNode | null = null;
let mixerNode: GainNode | null = null;
let sinkNode: GainNode | null = null;
let videoRoom: Room | null = null;
let voiceCall: Call | null = null;
let voiceRemoteSourceNode: MediaStreamAudioSourceNode | null = null;
let isLocalMutedState = false;

let audioBuffer: Float32Array[] = [];
let chunkDurationSec: number;
let accumulatedSamples = 0;
const SAMPLE_RATE = 16000;
const MUTE_RAMP_SEC = 0.02;
// LocalParticipant has no 'trackUnpublished'; 'trackStopped' is what a removed microphone track reports.
const LOCAL_TRACK_EVENTS = ['trackDisabled', 'trackEnabled', 'trackPublished', 'trackStopped'];
const remoteSources = new Map<string, MediaStreamAudioSourceNode>();
const roomCleanup: Array<() => void> = [];

const listeners: Record<string, Listener[]> = {
  isRecording: [],
  isAudioError: [],
  chunk: [],
  isRecordingPaused: []
};

const updateListener = (event: string, value: any) => {
  listeners[event].forEach((listener) => listener(value));
};

const clearRoomCleanup = () => {
  while (roomCleanup.length > 0) {
    const cleanup = roomCleanup.pop();
    cleanup?.();
  }
};

const detachRemoteSource = (key: string) => {
  const source = remoteSources.get(key);
  if (!source) return;
  source.disconnect();
  remoteSources.delete(key);
};

const detachAllRemoteSources = () => {
  remoteSources.forEach((source) => source.disconnect());
  remoteSources.clear();
};

const detachVoiceRemoteSource = () => {
  if (!voiceRemoteSourceNode) return;
  voiceRemoteSourceNode.disconnect();
  voiceRemoteSourceNode = null;
};

const remoteSourceKey = (participantSid?: string, trackSid?: string, mediaStreamTrack?: MediaStreamTrack) => {
  if (trackSid) return trackSid;
  if (participantSid && mediaStreamTrack?.id) return `${participantSid}:${mediaStreamTrack.id}`;
  if (mediaStreamTrack?.id) return mediaStreamTrack.id;
  return null;
};

const attachRemoteTrack = (
  participantSid: string | undefined,
  publication: RemotePublicationLike,
  track = publication.track
) => {
  if (!audioContext || !mixerNode || !track?.mediaStreamTrack) return;
  if (track.kind && track.kind !== 'audio') return;
  const key = remoteSourceKey(participantSid, publication.trackSid, track.mediaStreamTrack);
  if (!key || remoteSources.has(key)) return;

  const remoteStream = new MediaStream([track.mediaStreamTrack]);
  const remoteSource = audioContext.createMediaStreamSource(remoteStream);
  remoteSource.connect(mixerNode);
  remoteSources.set(key, remoteSource);
};

const detachParticipantAudio = (participant: RemoteParticipantLike) => {
  participant.audioTracks?.forEach((publication) => {
    const key = remoteSourceKey(participant.sid, publication.trackSid, publication.track?.mediaStreamTrack);
    if (key) detachRemoteSource(key);
  });
};

const bindParticipantAudio = (participant: RemoteParticipantLike) => {
  participant.audioTracks?.forEach((publication) => {
    attachRemoteTrack(participant.sid, publication);
  });

  const onTrackSubscribed = (track: RemotePublicationLike['track'], publication: RemotePublicationLike) => {
    attachRemoteTrack(participant.sid, publication, track);
  };

  const onTrackUnsubscribed = (track: RemotePublicationLike['track'], publication: RemotePublicationLike) => {
    const key = remoteSourceKey(participant.sid, publication.trackSid, track?.mediaStreamTrack);
    if (key) detachRemoteSource(key);
  };

  participant.on?.('trackSubscribed', onTrackSubscribed);
  participant.on?.('trackUnsubscribed', onTrackUnsubscribed);

  roomCleanup.push(() => {
    participant.removeListener?.('trackSubscribed', onTrackSubscribed);
    participant.removeListener?.('trackUnsubscribed', onTrackUnsubscribed);
  });
};

const applyLocalMute = () => {
  if (!audioContext || !localGainNode) return;

  const target = isLocalMutedState ? 0 : 1;
  const { gain } = localGainNode;
  const { currentTime } = audioContext;

  gain.cancelScheduledValues(currentTime);
  gain.setValueAtTime(gain.value, currentTime);
  gain.linearRampToValueAtTime(target, currentTime + MUTE_RAMP_SEC);
};

const setLocalMuted = (isMuted: boolean) => {
  if (isLocalMutedState === isMuted) return;
  isLocalMutedState = isMuted;
  applyLocalMute();
};

const getLocalParticipant = () => videoRoom?.localParticipant as unknown as LocalParticipantLike | undefined;

const readLocalMuteFromRoom = () => {
  let hasEnabledTrack = false;
  let hasAudioTrack = false;

  getLocalParticipant()?.audioTracks?.forEach((publication) => {
    const track = publication?.track;
    if (!track || (track.kind && track.kind !== 'audio')) return;
    hasAudioTrack = true;
    if (track.isEnabled !== false) hasEnabledTrack = true;
  });

  setLocalMuted(hasAudioTrack && !hasEnabledTrack);
};

const bindLocalAudioMute = () => {
  const localParticipant = getLocalParticipant();
  if (!localParticipant) return;

  const onLocalTracksChanged = () => readLocalMuteFromRoom();

  LOCAL_TRACK_EVENTS.forEach((event) => localParticipant.on?.(event, onLocalTracksChanged));

  roomCleanup.push(() => {
    LOCAL_TRACK_EVENTS.forEach((event) => localParticipant.removeListener?.(event, onLocalTracksChanged));
  });

  readLocalMuteFromRoom();
};

const syncVideoRoomBindings = () => {
  clearRoomCleanup();
  detachAllRemoteSources();

  if (!videoRoom) {
    // No room means no mute control, so never leave the local branch gated shut.
    setLocalMuted(false);
    return;
  }

  bindLocalAudioMute();

  if (!audioContext || !mixerNode) return;
  const activeRoom = videoRoom;

  const onParticipantConnected = (participant: RemoteParticipantLike) => {
    bindParticipantAudio(participant);
  };

  const onParticipantDisconnected = (participant: RemoteParticipantLike) => {
    detachParticipantAudio(participant);
  };

  activeRoom.participants.forEach((participant) => {
    bindParticipantAudio(participant);
  });

  activeRoom.on('participantConnected', onParticipantConnected);
  activeRoom.on('participantDisconnected', onParticipantDisconnected);

  roomCleanup.push(() => {
    activeRoom.removeListener('participantConnected', onParticipantConnected);
    activeRoom.removeListener('participantDisconnected', onParticipantDisconnected);
  });
};

const syncVoiceCallBinding = () => {
  detachVoiceRemoteSource();

  if (!voiceCall || !audioContext || !mixerNode) return;

  const remoteStream = voiceCall.getRemoteStream();
  if (!remoteStream) return;

  voiceRemoteSourceNode = audioContext.createMediaStreamSource(remoteStream);
  voiceRemoteSourceNode.connect(mixerNode);
};

const handleAudioData = (pcmData: Float32Array) => {
  if (!audioContext || !chunkDurationSec || chunkDurationSec <= 0) return;

  audioBuffer.push(pcmData);
  accumulatedSamples += pcmData.length;

  const targetSamples = SAMPLE_RATE * chunkDurationSec;

  if (accumulatedSamples >= targetSamples) {
    console.log(`[AudioService] Bucket full (${accumulatedSamples} samples). Processing ${chunkDurationSec}s chunk.`);
    processChunk();
  }
};

const processChunk = async () => {
  if (accumulatedSamples === 0) return;

  const combinedData = flattenBuffer(audioBuffer, accumulatedSamples);

  // Reset buffer immediately to catch next chunk seamlessly
  audioBuffer = [];
  accumulatedSamples = 0;

  // convert PCM to WAV Blob
  const wavBlob = encodeToWav(combinedData, SAMPLE_RATE);
  updateListener('chunk', wavBlob);
};

const flattenBuffer = (buffer: Float32Array[], totalLength: number): Float32Array => {
  const result = new Float32Array(totalLength);
  let offset = 0;
  for (const arr of buffer) {
    result.set(arr, offset);
    offset = offset + arr.length;
  }
  return result;
};

// WAV header Generator
// Raw PCM data is unplayable by default so must be prefixed with standard RIFF/WAVE header.
// It tells media players how to decode it
const encodeToWav = (samples: Float32Array, sampleRate: number): Blob => {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  const writeString = (view: DataView, offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  // RIFF chunk descriptor
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(view, 8, 'WAVE');

  // Format sub-chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);

  // Data sub-chunk
  writeString(view, 36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++, offset += 2) {
    // Web Audio API provides samples as Float32 values (between -1 and 1)
    // WAV files expect 16bit integers so we scale up
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return new Blob([view], { type: 'audio/wav' });
};

export const audioTranscriptionService = {
  // Encapsulation: We use a getter to safely expose the internal state.
  // React can read this to update the UI, but only selected
  // methods can modify the state.
  get isRecording() {
    return isRecordingState;
  },

  get isAudioError() {
    return isAudioErrorState;
  },

  get isRecordingPaused() {
    return isRecordingPausedState;
  },

  setAudioError: (isAudioError: boolean) => {
    isAudioErrorState = isAudioError;
    updateListener('isAudioError', isAudioError);
  },

  setIsRecordingPaused: (isRecordingPaused: boolean) => {
    isRecordingPausedState = isRecordingPaused;
    updateListener('isRecordingPaused', isRecordingPaused);
  },

  subscribe: (event: string, callback: Listener) => {
    listeners[event].push(callback);
  },

  unsubscribe: (event: string, callback: Listener) => {
    listeners[event] = listeners[event].filter((listener) => listener !== callback);
  },

  setVideoRoom: (room: Room | null) => {
    videoRoom = room;
    syncVideoRoomBindings();
  },

  setVoiceCall: (call: Call | null) => {
    voiceCall = call;
    syncVoiceCallBinding();
  },

  start: async (configChunkSecs: number) => {
    if (isRecordingState || isInitializing) return;

    isInitializing = true;
    chunkDurationSec = configChunkSecs;

    audioBuffer = [];
    accumulatedSamples = 0;

    try {
      if (audioContext) {
        await audioContext.close().catch(() => {});
      }

      mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, autoGainControl: false, noiseSuppression: false }
      });

      audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });

      // AUDIO WORKLET
      // Using a worklet allows real-time audio processing to happen on a
      // background thread so it's reliable and consistent.
      // It still communicates with the main thread.
      const workletCode = ` 
            class RecorderWorklet extends AudioWorkletProcessor {
                process (inputs, outputs, parameters) {
                    const input = inputs[0];
                    if (input.length > 0) {
                        this.port.postMessage(Float32Array.from(input[0]))
                    }
                    return true
                }
            }
            registerProcessor('recorder-worklet', RecorderWorklet)
           `;

      const blob = new Blob([workletCode], { type: 'application/javascript' });
      const workletUrl = URL.createObjectURL(blob);

      // Load virtual file into the Audio Context
      await audioContext.audioWorklet.addModule(workletUrl);
      URL.revokeObjectURL(workletUrl);

      if (!isInitializing) return;

      const source = audioContext.createMediaStreamSource(mediaStream);
      const localGain = audioContext.createGain();
      const mixer = audioContext.createGain();
      const sink = audioContext.createGain();
      workletNode = new AudioWorkletNode(audioContext, 'recorder-worklet');
      localSourceNode = source;
      localGainNode = localGain;
      mixerNode = mixer;
      sinkNode = sink;
      sink.gain.value = 0;
      localGain.gain.value = isLocalMutedState ? 0 : 1;

      // Listen for messages the background thread is posting
      workletNode.port.onmessage = (event) => {
        handleAudioData(event.data);
      };

      source.connect(localGain);
      localGain.connect(mixer);
      syncVideoRoomBindings();
      syncVoiceCallBinding();
      mixer.connect(workletNode);
      workletNode.connect(sink);
      sink.connect(audioContext.destination);

      isRecordingState = true;
      updateListener('isRecording', true);
      console.log(`[AudioService] Successfully started with ${configChunkSecs}s intervals.`);
    } catch (err) {
      console.error('[AudioService] Start Error:', err);
      isAudioErrorState = true;
      updateListener('isAudioError', true);
    } finally {
      isInitializing = false;
    }
  },

  stop: () => {
    isInitializing = false;

    if (isRecordingState && accumulatedSamples > 0) {
      console.log('[AudioService] Finalizing remaining audio buffer.');
      processChunk();
    } else {
      audioBuffer = [];
      accumulatedSamples = 0;
    }

    clearRoomCleanup();
    detachAllRemoteSources();
    detachVoiceRemoteSource();

    if (localSourceNode) {
      localSourceNode.disconnect();
      localSourceNode = null;
    }

    if (localGainNode) {
      localGainNode.disconnect();
      localGainNode = null;
    }

    if (mixerNode) {
      mixerNode.disconnect();
      mixerNode = null;
    }

    if (workletNode) {
      workletNode.disconnect();
      workletNode = null;
    }

    if (sinkNode) {
      sinkNode.disconnect();
      sinkNode = null;
    }

    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
      mediaStream = null;
    }

    if (audioContext) {
      audioContext.close();
      audioContext = null;
    }

    isRecordingState = false;
    isRecordingPausedState = false;
    updateListener('isRecording', false);
    updateListener('isRecordingPaused', false);
  }
};
